import { useMemo } from 'react';
import { Marker, CircleMarker, Polygon, Polyline, Rectangle } from 'react-leaflet';
import L from 'leaflet';
import { getCategoryIcon } from '../../../data/resources';
import { getServiceIcon } from '../../../data/gisServices';

const EGYPT_BOUNDS = {
  minLat: 22.0,
  maxLat: 31.7,
  minLng: 24.5,
  maxLng: 36.9
};

const KIDS_ICONS = ['⭐', '🧭', '📍', '🌞', '💧', '🌾', '⛏️', '🏛️', '🎒', '🧠', '🧩', '🪄'];
const CLASSIC_ICONS = ['⭐', '🧭', '📍', '🏜️', '🛰️', '💧', '🌾', '⛏️', '🏛️', '🧱', '🗺️', '🪨'];

const KIDS_COLORS = ['#38bdf8', '#fb7185', '#34d399', '#f59e0b', '#a78bfa', '#2dd4bf'];
const CLASSIC_COLORS = ['#D4AF37', '#1E88E5', '#66BB6A', '#FFA726', '#8E24AA', '#26A69A'];

const createArtIcon = (emoji, color, kidMode, seed = 0) => {
  const delay = ((seed % 9) * 0.22).toFixed(2);
  const duration = (2.1 + (seed % 5) * 0.42).toFixed(2);

  return L.divIcon({
    html: `<div class="map-art-marker ${kidMode ? 'map-art-marker-kid map-art-marker-float' : ''}" style="--float-delay:${delay}s;--float-duration:${duration}s;border-color:${color};color:${color};">${emoji}</div>`,
    className: 'map-art-marker-wrapper',
    iconSize: [kidMode ? 30 : 26, kidMode ? 30 : 26],
    iconAnchor: [kidMode ? 15 : 13, kidMode ? 15 : 13]
  });
};

const jitter = (seed) => (Math.sin(seed) * 0.28);

const buildGridPoints = (kidMode) => {
  const points = [];
  const latStep = kidMode ? 1.6 : 1.4;
  const lngStep = kidMode ? 1.8 : 1.6;
  const icons = kidMode ? KIDS_ICONS : CLASSIC_ICONS;
  const colors = kidMode ? KIDS_COLORS : CLASSIC_COLORS;

  let index = 0;
  for (let lat = EGYPT_BOUNDS.minLat + 0.3; lat <= EGYPT_BOUNDS.maxLat - 0.3; lat += latStep) {
    for (let lng = EGYPT_BOUNDS.minLng + 0.4; lng <= EGYPT_BOUNDS.maxLng - 0.4; lng += lngStep) {
      const seed = (lat * 1000 + lng * 100) * 0.13;
      points.push({
        id: `art-${index}`,
        lat: lat + jitter(seed),
        lng: lng + jitter(seed * 1.7),
        icon: icons[index % icons.length],
        color: colors[index % colors.length]
      });
      index += 1;
    }
  }

  return points;
};

const buildLines = (kidMode) => ([
  {
    id: 'nile-ribbon',
    color: kidMode ? '#38bdf8' : '#1E88E5',
    weight: kidMode ? 5 : 4,
    dashArray: '6 8',
    points: [
      [30.2, 31.2],
      [29.5, 30.9],
      [28.3, 30.6],
      [27.2, 30.3],
      [26.2, 30.1],
      [25.1, 29.9]
    ]
  },
  {
    id: 'redsea-band',
    color: kidMode ? '#34d399' : '#26A69A',
    weight: kidMode ? 4 : 3,
    dashArray: '4 6',
    points: [
      [29.7, 34.8],
      [28.4, 34.3],
      [27.2, 34.0],
      [25.8, 34.1],
      [24.6, 34.5]
    ]
  }
]);

const buildPolygons = (kidMode) => ([
  {
    id: 'delta-shape',
    color: kidMode ? '#34d399' : '#66BB6A',
    points: [
      [31.7, 30.0],
      [31.0, 29.7],
      [30.6, 31.0],
      [31.1, 32.2]
    ]
  },
  {
    id: 'sinai-shape',
    color: kidMode ? '#f59e0b' : '#FFA726',
    points: [
      [30.9, 33.4],
      [29.1, 34.8],
      [28.1, 33.0],
      [29.7, 32.4]
    ]
  }
]);

const buildRectangles = (kidMode) => ([
  {
    id: 'west-desert',
    color: kidMode ? '#fb7185' : '#D4AF37',
    bounds: [
      [25.3, 25.8],
      [27.8, 29.4]
    ]
  },
  {
    id: 'upper-egypt',
    color: kidMode ? '#a78bfa' : '#8E24AA',
    bounds: [
      [24.0, 30.3],
      [26.4, 32.1]
    ]
  }
]);

export const MapArtLayer = ({ resources = [], services = [], show = true, pane = 'art', kidMode = true }) => {
  const artPoints = useMemo(() => buildGridPoints(kidMode), [kidMode]);
  const artLines = useMemo(() => buildLines(kidMode), [kidMode]);
  const artPolygons = useMemo(() => buildPolygons(kidMode), [kidMode]);
  const artRectangles = useMemo(() => buildRectangles(kidMode), [kidMode]);
  const palette = kidMode ? KIDS_COLORS : CLASSIC_COLORS;

  const resourceHalos = useMemo(
    () =>
      resources.map((resource, index) => ({
        id: `resource-halo-${resource.id}`,
        center: [resource.lat, resource.lng],
        radius: 8 + (resource.importance || 3) * (kidMode ? 2.6 : 2),
        color: palette[index % palette.length],
        icon: getCategoryIcon(resource.type),
        waveClass: `map-art-halo-wave-${index % 3}`
      })),
    [kidMode, palette, resources]
  );

  const serviceHalos = useMemo(
    () =>
      services.map((service, index) => ({
        id: `service-halo-${service.id}`,
        center: [service.lat, service.lng],
        radius: kidMode ? 12 : 10,
        color: palette[(index + 2) % palette.length],
        icon: getServiceIcon(service.type),
        waveClass: `map-art-halo-wave-${index % 3}`
      })),
    [kidMode, palette, services]
  );

  if (!show) return null;

  return (
    <>
      {artPolygons.map((shape) => (
        <Polygon
          key={shape.id}
          positions={shape.points}
          pane={pane}
          pathOptions={{
            color: shape.color,
            weight: 2,
            fillOpacity: kidMode ? 0.14 : 0.1,
            interactive: false,
            className: kidMode ? 'map-art-shape map-art-shape-kid' : 'map-art-shape'
          }}
        />
      ))}

      {artRectangles.map((shape) => (
        <Rectangle
          key={shape.id}
          bounds={shape.bounds}
          pane={pane}
          pathOptions={{
            color: shape.color,
            weight: 2,
            fillOpacity: kidMode ? 0.12 : 0.08,
            dashArray: '6 8',
            interactive: false,
            className: kidMode ? 'map-art-zone map-art-zone-kid' : 'map-art-zone'
          }}
        />
      ))}

      {artLines.map((line) => (
        <Polyline
          key={line.id}
          positions={line.points}
          pane={pane}
          pathOptions={{
            color: line.color,
            weight: line.weight,
            dashArray: line.dashArray,
            interactive: false,
            className: kidMode ? 'map-art-flow-line map-art-flow-line-kid' : 'map-art-flow-line'
          }}
        />
      ))}

      {resourceHalos.map((halo) => (
        <CircleMarker
          key={halo.id}
          center={halo.center}
          radius={halo.radius}
          pane={pane}
          pathOptions={{
            color: halo.color,
            fillColor: halo.color,
            fillOpacity: kidMode ? 0.24 : 0.2,
            interactive: false,
            className: `map-art-halo map-art-halo-resource ${halo.waveClass}`
          }}
        />
      ))}

      {serviceHalos.map((halo) => (
        <CircleMarker
          key={halo.id}
          center={halo.center}
          radius={halo.radius}
          pane={pane}
          pathOptions={{
            color: halo.color,
            fillColor: halo.color,
            fillOpacity: kidMode ? 0.22 : 0.16,
            interactive: false,
            className: `map-art-halo map-art-halo-service ${halo.waveClass}`
          }}
        />
      ))}

      {artPoints.map((point, index) => (
        <Marker
          key={point.id}
          position={[point.lat, point.lng]}
          pane={pane}
          zIndexOffset={-200}
          icon={createArtIcon(point.icon, point.color, kidMode, index)}
          opacity={0.9}
          interactive={false}
        />
      ))}

      {resourceHalos.map((halo, index) => (
        <Marker
          key={`${halo.id}-icon`}
          position={halo.center}
          pane={pane}
          zIndexOffset={-200}
          icon={createArtIcon(halo.icon, halo.color, kidMode, index + 70)}
          opacity={0.95}
          interactive={false}
        />
      ))}

      {serviceHalos.map((halo, index) => (
        <Marker
          key={`${halo.id}-icon`}
          position={halo.center}
          pane={pane}
          zIndexOffset={-200}
          icon={createArtIcon(halo.icon, halo.color, kidMode, index + 140)}
          opacity={0.9}
          interactive={false}
        />
      ))}
    </>
  );
};
